import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import stories from "../../../../utils/storyData";

const levelEmojis = {
  moderate: "🧘‍♂️",
  difficult: "🧗‍♀️",
  hard: "🐉",
};

const VoiceQuest = () => {
  const { t } = useTranslation();

  const [level, setLevel] = useState(null);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [showQuestions, setShowQuestions] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [score, setScore] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const audioRef = useRef(null);

  // --- Handlers ---
  const handleLevelSelect = (lvl) => {
    setLevel(lvl);
    setCurrentStoryIndex(0);
    setShowQuestions(false);
    setSelectedAnswers({});
    setScore(null);
    setHasPlayed(false);
  };

  const handleBackToLevels = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setLevel(null);
    setIsPlaying(false);
    setHasPlayed(false);
  };

  const getPlaybackRate = (lvl) => {
  switch (lvl) {
    case "moderate":
      return 0.80;      
    case "difficult":
      return 0.90;
    case "hard":
      return 0.95;
    default:
      return 0.85;
  }
  };

  const handlePlayStory = () => {
    setShowQuestions(false);
    setSelectedAnswers({});
    setScore(null);

    if (audioRef.current) {
      const audio = audioRef.current;

      audio.playbackRate = getPlaybackRate(level);

      // ✅ Prevent robotic / cut sound
      audio.preservesPitch = true;
      audio.mozPreservesPitch = true;
      audio.webkitPreservesPitch = true;

      setIsPlaying(true);
      setHasPlayed(true);

      audio.play();
      audio.onended = () => {
        setIsPlaying(false);
        setShowQuestions(true);
      };
    }
  };

  const handleAnswer = (qIndex, option) => {
    if (score === null) {
      setSelectedAnswers({ ...selectedAnswers, [qIndex]: option });
    }
  };

  const handleSubmit = (story) => {
    let count = 0;
    story.questions.forEach((q, i) => {
      if (selectedAnswers[i] === q.answer) count++;
    });
    setScore(count);
  };

  const handleNextStory = (storyListLength) => {
    setSelectedAnswers({});
    setScore(null);
    setShowQuestions(false);
    setHasPlayed(false);

    if (currentStoryIndex < storyListLength - 1) {
      setCurrentStoryIndex(currentStoryIndex + 1);
    } else {
      handleBackToLevels();
    }
  };

  const formatLevelName = (lvl) => t(`voiceQuest.levels.${lvl}`);
  const formatLevelDescription = (lvl) => t(`voiceQuest.levelInfo.${lvl}`);

  // --- Level Selection ---
  if (!level) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 p-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-indigo-800 mb-4">
            {t("voiceQuest.title")}
          </h2>
          <p className="text-xl text-gray-700 mb-10">{t("voiceQuest.subTitle")}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.keys(stories).map((lvl) => (
              <div
                key={lvl}
                onClick={() => handleLevelSelect(lvl)}
                className="bg-white p-8 rounded-2xl shadow-lg cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
              >
                <span className="text-5xl" role="img" aria-label={lvl}>
                  {levelEmojis[lvl]}
                </span>
                <h3 className="text-2xl font-semibold text-indigo-700 my-4">
                  {formatLevelName(lvl)}
                </h3>
                <p className="text-gray-600">{formatLevelDescription(lvl)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // --- Story & Quiz ---
  const storyList = stories[level];
  const story = storyList[currentStoryIndex];
  const isLastStory = currentStoryIndex === storyList.length - 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 p-4 md:p-8">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden p-6 md:p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-5">
          <button
            onClick={handleBackToLevels}
            className="text-sm font-medium text-indigo-600 hover:underline"
          >
            &larr; {t("voiceQuest.changeLevel")}
          </button>
          <span className="px-4 py-1 bg-indigo-100 text-indigo-800 font-semibold rounded-full text-sm">
            {formatLevelName(level)}
          </span>
        </div>

        {/* Story Card */}
        <div className="bg-gray-50 p-6 rounded-xl mb-6">
          <p className="text-sm font-semibold text-indigo-500 mb-2">
            {t("voiceQuest.storyCount", {
              current: currentStoryIndex + 1,
              total: storyList.length,
            })}
          </p>
          <h3 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            {story.title}
          </h3>

          <audio ref={audioRef} src={story.audioUrl} preload="auto" />

          <div className="text-center">
            <button
              onClick={handlePlayStory}
              disabled={isPlaying || hasPlayed}
              className={`px-8 py-3 rounded-full text-white text-lg font-semibold shadow-md transition-all duration-300 ${
                isPlaying
                  ? "bg-gray-400 cursor-not-allowed"
                  : hasPlayed
                  ? "bg-indigo-300 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700 transform hover:scale-105"
              }`}
            >
              {isPlaying
                ? t("voiceQuest.listening")
                : hasPlayed
                ? t("voiceQuest.listenComplete")
                : t("voiceQuest.playStory")}
            </button>
            {!hasPlayed && (
              <p className="text-sm text-gray-600 mt-3">{t("voiceQuest.playHint")}</p>
            )}
          </div>
        </div>

        {/* Questions */}
        {showQuestions && (
          <div className="mt-6">
            <h4 className="text-2xl font-bold mb-4 text-gray-800">
              {t("voiceQuest.quizTitle")}
            </h4>

            {story.questions.map((q, i) => (
              <div key={i} className="mb-6 p-5 bg-white shadow-md rounded-lg">
                <p className="font-semibold text-lg text-gray-900 mb-4">
                  {i + 1}. {q.question}
                </p>
                <div className="grid grid-cols-1 gap-3">
                  {q.options.map((opt, j) => {
                    const isSelected = selectedAnswers[i] === opt;
                    const isCorrect = q.answer === opt;

                    let buttonStyle =
                      "border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-indigo-400";
                    if (score !== null) {
                      if (isCorrect)
                        buttonStyle =
                          "bg-green-100 border-green-500 text-green-900 font-semibold";
                      else if (isSelected && !isCorrect)
                        buttonStyle = "bg-red-100 border-red-500 text-red-900";
                      else buttonStyle = "bg-gray-100 border-gray-300 text-gray-600";
                    } else if (isSelected) {
                      buttonStyle =
                        "border-indigo-500 bg-indigo-100 ring-2 ring-indigo-300";
                    }

                    return (
                      <button
                        key={j}
                        onClick={() => handleAnswer(i, opt)}
                        disabled={score !== null}
                        className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left font-medium ${buttonStyle} ${
                          score === null ? "cursor-pointer" : "cursor-default"
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="mt-8 text-center">
              {score === null ? (
                <button
                  onClick={() => handleSubmit(story)}
                  className="w-full md:w-auto px-10 py-3 bg-green-600 hover:bg-green-700 text-white text-lg font-semibold rounded-full shadow-lg transition-transform transform hover:scale-105"
                >
                  {t("voiceQuest.submitAnswers")}
                </button>
              ) : (
                <div className="text-center p-6 bg-blue-50 rounded-xl">
                  <p className="text-2xl font-bold text-indigo-800">
                    {t("voiceQuest.score", { score, total: story.questions.length })}
                  </p>
                  <button
                    onClick={() => handleNextStory(storyList.length)}
                    className="mt-4 px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white text-lg font-semibold rounded-full shadow-lg transition-transform transform hover:scale-105"
                  >
                    {isLastStory
                      ? t("voiceQuest.finishLevel")
                      : t("voiceQuest.nextStory")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceQuest;
